import {
  BuildingOfficeIcon,
  CalendarBlankIcon,
  ChartLineUpIcon,
  ChatsCircleIcon,
  CheckSquareIcon,
  ClipboardTextIcon,
  CurrencyDollarIcon,
  EnvelopeSimpleOpenIcon,
  FilesIcon,
  FileTextIcon,
  FunnelIcon,
  GearSixIcon,
  GitBranchIcon,
  HandshakeIcon,
  KanbanIcon,
  LayoutIcon,
  LightningIcon,
  ListChecksIcon,
  PackageIcon,
  PaperPlaneTiltIcon,
  PuzzlePieceIcon,
  ScalesIcon,
  ShieldCheckIcon,
  SparkleIcon,
  StackIcon,
  TagIcon,
  TargetIcon,
  TicketIcon,
  TrendUpIcon,
  TruckIcon,
  UsersThreeIcon,
} from '@phosphor-icons/react'

import type { ModuleLandingConfig } from './ModuleUpgradeLanding'

interface SupplierLandingTerminology {
  singular: string
  plural: string
  Singular: string
  Plural: string
}

interface ModuleLandingConfigOptions {
  supplierTerminology?: SupplierLandingTerminology
}

const DEFAULT_SUPPLIER_LANDING_TERMINOLOGY: SupplierLandingTerminology = {
  singular: 'vendor',
  plural: 'vendors',
  Singular: 'Vendor',
  Plural: 'Vendors',
}

const forms: ModuleLandingConfig = {
  slug: 'forms',
  title: 'Forms',
  subtitle:
    'Capture contacts, collect feedback, and turn every submission into action — directly inside the workspace your team already lives in.',
  icon: ClipboardTextIcon,
  accent: 'sky',
  features: [
    {
      icon: LayoutIcon,
      title: 'Drag-and-drop builder',
      body: 'Compose multi-section forms with conditional logic in minutes. No designer required.',
    },
    {
      icon: PaperPlaneTiltIcon,
      title: 'Public + internal flows',
      body: 'Publish to the web, embed on a page, or scope a form to a single team — all from one record.',
    },
    {
      icon: PuzzlePieceIcon,
      title: 'Convert on submit',
      body: 'Pipe submissions into Contacts, Tickets, Deals, or Tasks automatically. Zero glue code.',
    },
    {
      icon: ShieldCheckIcon,
      title: 'reCAPTCHA & ownership',
      body: 'Protect public forms from spam and assign every response to the right teammate by default.',
    },
  ],
  preview: {
    label: 'forms / all',
    columns: [
      { key: 'name', label: 'Form' },
      { key: 'submissions', label: 'Submissions', align: 'right' },
      { key: 'status', label: 'Status' },
      { key: 'updated', label: 'Updated' },
    ],
    rows: [
      {
        id: '1',
        avatar: { initials: 'OS', tone: 'sky' },
        cells: {
          name: 'Onboarding Survey',
          submissions: { kind: 'money', value: '142' },
          status: { kind: 'pill', label: 'Active', tone: 'mint' },
          updated: { kind: 'muted', value: '2 days ago' },
        },
      },
      {
        id: '2',
        avatar: { initials: 'CF', tone: 'mint' },
        cells: {
          name: 'Customer Feedback Q4',
          submissions: { kind: 'money', value: '89' },
          status: { kind: 'pill', label: 'Active', tone: 'mint' },
          updated: { kind: 'muted', value: '1 week ago' },
        },
      },
      {
        id: '3',
        avatar: { initials: 'ER', tone: 'rose' },
        cells: {
          name: 'Event RSVP',
          submissions: { kind: 'money', value: '34' },
          status: { kind: 'pill', label: 'Closed', tone: 'slate' },
          updated: { kind: 'muted', value: '3 weeks ago' },
        },
      },
      {
        id: '4',
        avatar: { initials: 'JA', tone: 'lilac' },
        cells: {
          name: 'Job Application',
          submissions: { kind: 'money', value: '56' },
          status: { kind: 'pill', label: 'Active', tone: 'mint' },
          updated: { kind: 'muted', value: 'Yesterday' },
        },
      },
      {
        id: '5',
        avatar: { initials: 'PB', tone: 'amber' },
        cells: {
          name: 'Product Beta Signup',
          submissions: { kind: 'money', value: '211' },
          status: { kind: 'pill', label: 'Active', tone: 'mint' },
          updated: { kind: 'muted', value: '4 hours ago' },
        },
      },
    ],
  },
}

const contacts: ModuleLandingConfig = {
  slug: 'contacts',
  title: 'Contacts',
  subtitle:
    'A pipeline that knows the difference between a maybe and a yes — and helps your team get more of the latter.',
  icon: FunnelIcon,
  accent: 'orange',
  features: [
    {
      icon: KanbanIcon,
      title: 'Stages your way',
      body: 'Configure stages, win/loss reasons, and required fields per pipeline. No spreadsheet exports needed.',
    },
    {
      icon: TargetIcon,
      title: 'Owner-aware routing',
      body: 'Round-robin assignment, ownership transfer, and audit history — all built in.',
    },
    {
      icon: ChartLineUpIcon,
      title: 'Forecasting',
      body: 'See weighted pipeline value by stage, owner, and time horizon at a glance.',
    },
    {
      icon: HandshakeIcon,
      title: 'Convert to Deal',
      body: 'Promote a qualified contact into a deal with one click, dragging context along for the ride.',
    },
  ],
  preview: {
    label: 'contacts / qualified',
    columns: [
      { key: 'name', label: 'Contact' },
      { key: 'company', label: 'Company' },
      { key: 'value', label: 'Value', align: 'right' },
      { key: 'stage', label: 'Stage' },
      { key: 'owner', label: 'Owner' },
    ],
    rows: [
      {
        id: '1',
        avatar: { initials: 'SC', tone: 'mint' },
        cells: {
          name: 'Sarah Chen',
          company: 'Acme Corp',
          value: { kind: 'money', value: '$45,000' },
          stage: { kind: 'pill', label: 'Qualified', tone: 'mint' },
          owner: { kind: 'muted', value: 'M.K.' },
        },
      },
      {
        id: '2',
        avatar: { initials: 'MR', tone: 'sky' },
        cells: {
          name: 'Marcus Rivera',
          company: 'Globex Inc',
          value: { kind: 'money', value: '$12,500' },
          stage: { kind: 'pill', label: 'New', tone: 'sky' },
          owner: { kind: 'muted', value: 'J.T.' },
        },
      },
      {
        id: '3',
        avatar: { initials: 'AP', tone: 'rose' },
        cells: {
          name: 'Aisha Patel',
          company: 'Initech',
          value: { kind: 'money', value: '$89,000' },
          stage: { kind: 'pill', label: 'Negotiation', tone: 'amber' },
          owner: { kind: 'muted', value: 'M.K.' },
        },
      },
      {
        id: '4',
        avatar: { initials: 'TW', tone: 'lilac' },
        cells: {
          name: 'Tom Walker',
          company: 'Hooli',
          value: { kind: 'money', value: '$24,000' },
          stage: { kind: 'pill', label: 'Contacted', tone: 'sky' },
          owner: { kind: 'muted', value: 'J.T.' },
        },
      },
      {
        id: '5',
        avatar: { initials: 'JN', tone: 'amber' },
        cells: {
          name: 'Jen Nakamura',
          company: 'Pied Piper',
          value: { kind: 'money', value: '$156,000' },
          stage: { kind: 'pill', label: 'Proposal', tone: 'lilac' },
          owner: { kind: 'muted', value: 'M.K.' },
        },
      },
    ],
  },
}

const deals: ModuleLandingConfig = {
  slug: 'deals',
  title: 'Deals',
  subtitle:
    'Where contacts become revenue. Track every opportunity from first conversation to closed-won — without losing the thread.',
  icon: HandshakeIcon,
  accent: 'emerald',
  features: [
    {
      icon: TrendUpIcon,
      title: 'Weighted pipeline',
      body: 'Probability per stage. Forecast revenue across owners, products, and quarters with a single view.',
    },
    {
      icon: GitBranchIcon,
      title: 'Multi-pipeline support',
      body: 'Run a sales pipeline alongside a renewals pipeline. Same UX, separate stages, isolated reports.',
    },
    {
      icon: ListChecksIcon,
      title: 'Required fields by stage',
      body: 'Force discovery answers before a deal can move to proposal. Clean data, automatically.',
    },
    {
      icon: SparkleIcon,
      title: 'AI deal summaries',
      body: 'Catch up on a stalled deal in 10 seconds — Benny summarizes notes, emails, and tasks in plain English.',
    },
  ],
  preview: {
    label: 'deals / open',
    columns: [
      { key: 'name', label: 'Deal' },
      { key: 'company', label: 'Company' },
      { key: 'value', label: 'Value', align: 'right' },
      { key: 'stage', label: 'Stage' },
      { key: 'close', label: 'Expected close' },
    ],
    rows: [
      {
        id: '1',
        avatar: { initials: 'AL', tone: 'mint' },
        cells: {
          name: 'Annual License Renewal',
          company: 'Acme Corp',
          value: { kind: 'money', value: '$48,000' },
          stage: { kind: 'pill', label: 'Closed Won', tone: 'mint' },
          close: { kind: 'muted', value: 'Q1 2026' },
        },
      },
      {
        id: '2',
        avatar: { initials: 'PE', tone: 'sky' },
        cells: {
          name: 'Platform Expansion',
          company: 'Globex',
          value: { kind: 'money', value: '$124,000' },
          stage: { kind: 'pill', label: 'Negotiation', tone: 'amber' },
          close: { kind: 'muted', value: 'Q2 2026' },
        },
      },
      {
        id: '3',
        avatar: { initials: 'NC', tone: 'rose' },
        cells: {
          name: 'New Customer',
          company: 'Stark Industries',
          value: { kind: 'money', value: '$67,000' },
          stage: { kind: 'pill', label: 'Proposal', tone: 'lilac' },
          close: { kind: 'muted', value: 'Q1 2026' },
        },
      },
      {
        id: '4',
        avatar: { initials: 'PU', tone: 'lilac' },
        cells: {
          name: 'Premium Upgrade',
          company: 'Wayne Enterprises',
          value: { kind: 'money', value: '$32,000' },
          stage: { kind: 'pill', label: 'Discovery', tone: 'sky' },
          close: { kind: 'muted', value: 'Q1 2026' },
        },
      },
      {
        id: '5',
        avatar: { initials: 'MC', tone: 'amber' },
        cells: {
          name: 'Multi-year Contract',
          company: 'Cyberdyne',
          value: { kind: 'money', value: '$245,000' },
          stage: { kind: 'pill', label: 'Negotiation', tone: 'amber' },
          close: { kind: 'muted', value: 'Q2 2026' },
        },
      },
    ],
  },
}

const team: ModuleLandingConfig = {
  slug: 'team',
  title: 'Team',
  subtitle:
    'Capacity planning, allocations, rate cards, and burn rates — the people side of project work, finally in one place.',
  icon: UsersThreeIcon,
  accent: 'violet',
  features: [
    {
      icon: ChartLineUpIcon,
      title: 'Capacity at a glance',
      body: 'See who is over, under, or perfectly allocated this week, this month, this quarter.',
    },
    {
      icon: CurrencyDollarIcon,
      title: 'Rate cards & cost',
      body: 'Bill rates, cost rates, and overrides per project. Profitability rolls up automatically.',
    },
    {
      icon: CalendarBlankIcon,
      title: 'Schedules & PTO',
      body: 'Weekly schedules, holidays, time-off requests — synced with allocations and forecasts.',
    },
    {
      icon: TargetIcon,
      title: 'Staffing decisions',
      body: 'Find the right person for the next role. Skills, availability, rate, location — all considered.',
    },
  ],
  preview: {
    label: 'team / planner',
    columns: [
      { key: 'name', label: 'Member' },
      { key: 'role', label: 'Role' },
      { key: 'utilization', label: 'Utilization', align: 'right' },
      { key: 'project', label: 'Current project' },
      { key: 'rate', label: 'Bill rate', align: 'right' },
    ],
    rows: [
      {
        id: '1',
        avatar: { initials: 'MK', tone: 'lilac' },
        cells: {
          name: 'Maria Kowalski',
          role: 'Senior Engineer',
          utilization: { kind: 'pill', label: '87%', tone: 'mint' },
          project: 'Project Apollo',
          rate: { kind: 'money', value: '$145/hr' },
        },
      },
      {
        id: '2',
        avatar: { initials: 'DP', tone: 'sky' },
        cells: {
          name: 'Daniel Park',
          role: 'Designer',
          utilization: { kind: 'pill', label: '62%', tone: 'amber' },
          project: 'Brand Refresh',
          rate: { kind: 'money', value: '$110/hr' },
        },
      },
      {
        id: '3',
        avatar: { initials: 'PS', tone: 'rose' },
        cells: {
          name: 'Priya Singh',
          role: 'Product Manager',
          utilization: { kind: 'pill', label: '95%', tone: 'rose' },
          project: 'Q1 Roadmap',
          rate: { kind: 'money', value: '$135/hr' },
        },
      },
      {
        id: '4',
        avatar: { initials: 'LO', tone: 'mint' },
        cells: {
          name: "Liam O'Connor",
          role: 'Engineer',
          utilization: { kind: 'pill', label: '41%', tone: 'sky' },
          project: { kind: 'muted', value: 'Available' },
          rate: { kind: 'money', value: '$120/hr' },
        },
      },
      {
        id: '5',
        avatar: { initials: 'ZA', tone: 'amber' },
        cells: {
          name: 'Zara Ahmed',
          role: 'Lead Designer',
          utilization: { kind: 'pill', label: '78%', tone: 'mint' },
          project: 'Mobile App v2',
          rate: { kind: 'money', value: '$140/hr' },
        },
      },
    ],
  },
}

const buildSuppliersLandingConfig = (
  terminology: SupplierLandingTerminology = DEFAULT_SUPPLIER_LANDING_TERMINOLOGY,
): ModuleLandingConfig => ({
  slug: 'suppliers',
  title: terminology.Plural,
  subtitle: `Every ${terminology.singular}, contract, and PO in one accountable system — with the receipts to back it up at audit time.`,
  icon: TruckIcon,
  accent: 'teal',
  features: [
    {
      icon: BuildingOfficeIcon,
      title: `${terminology.Singular} 360`,
      body: 'Contacts, certifications, payment terms, order history — everything you wish was on a single page.',
    },
    {
      icon: PackageIcon,
      title: 'Purchase orders',
      body: 'Issue, approve, receive. Track partial deliveries and reconcile to invoices in real time.',
    },
    {
      icon: ScalesIcon,
      title: 'Comparison & analysis',
      body: `Compare ${terminology.plural} per item by price, lead time, and reliability. Make sourcing data-driven.`,
    },
    {
      icon: ShieldCheckIcon,
      title: 'Audit-ready records',
      body: 'Versioned contracts, signature trails, and document retention that survives a procurement audit.',
    },
  ],
  preview: {
    label: `${terminology.plural} / preferred`,
    columns: [
      { key: 'name', label: terminology.Singular },
      { key: 'category', label: 'Category' },
      { key: 'orders', label: 'Orders', align: 'right' },
      { key: 'status', label: 'Status' },
      { key: 'terms', label: 'Terms' },
    ],
    rows: [
      {
        id: '1',
        avatar: { initials: 'FC', tone: 'slate' },
        cells: {
          name: 'Forge & Co. Materials',
          category: 'Steel · Raw Materials',
          orders: { kind: 'money', value: '23' },
          status: { kind: 'pill', label: 'Preferred', tone: 'mint' },
          terms: { kind: 'muted', value: 'NET 30' },
        },
      },
      {
        id: '2',
        avatar: { initials: 'PL', tone: 'sky' },
        cells: {
          name: 'Pinewood Logistics',
          category: 'Shipping',
          orders: { kind: 'money', value: '156' },
          status: { kind: 'pill', label: 'Active', tone: 'sky' },
          terms: { kind: 'muted', value: 'NET 15' },
        },
      },
      {
        id: '3',
        avatar: { initials: 'HO', tone: 'amber' },
        cells: {
          name: 'Helios Office Supply',
          category: 'Office',
          orders: { kind: 'money', value: '8' },
          status: { kind: 'pill', label: 'Active', tone: 'sky' },
          terms: { kind: 'muted', value: 'NET 30' },
        },
      },
      {
        id: '4',
        avatar: { initials: 'CP', tone: 'rose' },
        cells: {
          name: 'Crestwood Print',
          category: 'Marketing',
          orders: { kind: 'money', value: '45' },
          status: { kind: 'pill', label: 'Preferred', tone: 'mint' },
          terms: { kind: 'muted', value: 'NET 60' },
        },
      },
      {
        id: '5',
        avatar: { initials: 'AH', tone: 'lilac' },
        cells: {
          name: 'Atlas Hardware',
          category: 'Equipment',
          orders: { kind: 'money', value: '12' },
          status: { kind: 'pill', label: 'Pending', tone: 'amber' },
          terms: { kind: 'muted', value: 'NET 30' },
        },
      },
    ],
  },
})

const suppliers = buildSuppliersLandingConfig()

const contracts: ModuleLandingConfig = {
  slug: 'contracts',
  title: 'Contracts',
  subtitle:
    'Master agreements, SOWs, and retainers — versioned, dated, and never living in a stranger’s downloads folder.',
  icon: FileTextIcon,
  accent: 'indigo',
  features: [
    {
      icon: FilesIcon,
      title: 'Versioning, built-in',
      body: 'Every change tracked. Roll back to any prior version. See what changed between v3 and v4 in one click.',
    },
    {
      icon: CurrencyDollarIcon,
      title: 'Retainer plans',
      body: 'Recurring scopes with adjustments, scheduled changes, and overage tracking. Real money, real clarity.',
    },
    {
      icon: CalendarBlankIcon,
      title: 'Renewal radar',
      body: 'Get nudged 60, 30, and 7 days before any contract expires. Nobody loses an account on a technicality.',
    },
    {
      icon: ShieldCheckIcon,
      title: 'Signature ready',
      body: 'Send for e-signature, capture audit logs, and pin the executed copy to the record forever.',
    },
  ],
  preview: {
    label: 'contracts / active',
    columns: [
      { key: 'name', label: 'Contract' },
      { key: 'value', label: 'Value', align: 'right' },
      { key: 'status', label: 'Status' },
      { key: 'renews', label: 'Renews / Ends' },
    ],
    rows: [
      {
        id: '1',
        avatar: { initials: 'AC', tone: 'mint' },
        cells: {
          name: 'Acme Corp — Master Agreement',
          value: { kind: 'money', value: '$480,000/yr' },
          status: { kind: 'pill', label: 'Active', tone: 'mint' },
          renews: { kind: 'muted', value: 'Renews 2027-03-12' },
        },
      },
      {
        id: '2',
        avatar: { initials: 'GR', tone: 'sky' },
        cells: {
          name: 'Globex Service Retainer',
          value: { kind: 'money', value: '$24,000/mo' },
          status: { kind: 'pill', label: 'Active', tone: 'mint' },
          renews: { kind: 'muted', value: 'Auto-renews monthly' },
        },
      },
      {
        id: '3',
        avatar: { initials: 'PP', tone: 'amber' },
        cells: {
          name: 'Pied Piper — Implementation',
          value: { kind: 'money', value: '$145,000' },
          status: { kind: 'pill', label: 'Active', tone: 'mint' },
          renews: { kind: 'muted', value: 'Ends 2026-08-30' },
        },
      },
      {
        id: '4',
        avatar: { initials: 'HP', tone: 'lilac' },
        cells: {
          name: 'Hooli Partnership',
          value: { kind: 'muted', value: 'TBD' },
          status: { kind: 'pill', label: 'Draft', tone: 'slate' },
          renews: { kind: 'muted', value: 'Pending review' },
        },
      },
      {
        id: '5',
        avatar: { initials: 'SI', tone: 'rose' },
        cells: {
          name: 'Stark Industries SOW #4',
          value: { kind: 'money', value: '$87,500' },
          status: { kind: 'pill', label: 'Pending Sig.', tone: 'amber' },
          renews: { kind: 'muted', value: 'Sent 2 days ago' },
        },
      },
    ],
  },
}

const events: ModuleLandingConfig = {
  slug: 'events',
  title: 'Events',
  subtitle:
    'From a 12-person offsite to a 12,000-person conference — sessions, speakers, RSVPs, and the whole agenda in one place.',
  icon: CalendarBlankIcon,
  accent: 'rose',
  features: [
    {
      icon: ListChecksIcon,
      title: 'Multi-day agendas',
      body: 'Days, sessions, tracks, speakers, and breaks — modeled properly so the website doesn’t need a spreadsheet.',
    },
    {
      icon: EnvelopeSimpleOpenIcon,
      title: 'Invites & RSVPs',
      body: 'Send branded invites, track responses, manage waitlists. Approve or deny without losing your inbox.',
    },
    {
      icon: UsersThreeIcon,
      title: 'Speakers & hosts',
      body: 'Manage bios, headshots, sessions assigned, and travel logistics — all from the speaker record.',
    },
    {
      icon: ChartLineUpIcon,
      title: 'Capacity & analytics',
      body: 'See registrations vs capacity, no-show rates, and post-event surveys side by side.',
    },
  ],
  preview: {
    label: 'events / 2026',
    columns: [
      { key: 'name', label: 'Event' },
      { key: 'date', label: 'Date' },
      { key: 'invited', label: 'Invited', align: 'right' },
      { key: 'format', label: 'Format' },
      { key: 'status', label: 'Status' },
    ],
    rows: [
      {
        id: '1',
        avatar: { initials: 'CC', tone: 'rose' },
        cells: {
          name: 'Annual Customer Conference',
          date: { kind: 'muted', value: 'May 12, 2026' },
          invited: { kind: 'money', value: '340' },
          format: { kind: 'pill', label: 'Hybrid', tone: 'lilac' },
          status: { kind: 'pill', label: 'Confirmed', tone: 'mint' },
        },
      },
      {
        id: '2',
        avatar: { initials: 'SK', tone: 'amber' },
        cells: {
          name: 'Q1 Sales Kickoff',
          date: { kind: 'muted', value: 'Feb 8, 2026' },
          invited: { kind: 'money', value: '28' },
          format: { kind: 'pill', label: 'In-person · NYC', tone: 'sky' },
          status: { kind: 'pill', label: 'Confirmed', tone: 'mint' },
        },
      },
      {
        id: '3',
        avatar: { initials: 'PL', tone: 'sky' },
        cells: {
          name: 'Product Launch Webinar',
          date: { kind: 'muted', value: 'Apr 22, 2026' },
          invited: { kind: 'money', value: '1,200' },
          format: { kind: 'pill', label: 'Virtual', tone: 'sky' },
          status: { kind: 'pill', label: 'Draft', tone: 'slate' },
        },
      },
      {
        id: '4',
        avatar: { initials: 'PS', tone: 'mint' },
        cells: {
          name: 'Partner Summit',
          date: { kind: 'muted', value: 'Jun 15, 2026' },
          invited: { kind: 'money', value: '85' },
          format: { kind: 'pill', label: 'In-person · SF', tone: 'sky' },
          status: { kind: 'pill', label: 'Planning', tone: 'amber' },
        },
      },
      {
        id: '5',
        avatar: { initials: 'AH', tone: 'lilac' },
        cells: {
          name: 'Quarterly All-Hands',
          date: { kind: 'muted', value: 'Mar 30, 2026' },
          invited: { kind: 'money', value: '142' },
          format: { kind: 'pill', label: 'Hybrid', tone: 'lilac' },
          status: { kind: 'pill', label: 'Confirmed', tone: 'mint' },
        },
      },
    ],
  },
}

const tickets: ModuleLandingConfig = {
  slug: 'tickets',
  title: 'Tickets',
  subtitle:
    'A help desk that lives next to the work — so a customer issue can become a fix, an epic, or a deal in one move.',
  icon: TicketIcon,
  accent: 'fuchsia',
  features: [
    {
      icon: TagIcon,
      title: 'Priority + SLA',
      body: 'Per-ticket SLAs and breach alerts. Sort, filter, and route exactly the way your team triages.',
    },
    {
      icon: ChatsCircleIcon,
      title: 'Threaded replies',
      body: 'Comments, mentions, attachments — and a clear separation between internal notes and customer replies.',
    },
    {
      icon: GitBranchIcon,
      title: 'Convert anywhere',
      body: 'Promote a ticket into a task, a project, or a deal without losing context or the conversation.',
    },
    {
      icon: ChartLineUpIcon,
      title: 'Throughput dashboards',
      body: 'See first-response time, resolution time, and backlog by team or owner. No BI tool required.',
    },
  ],
  preview: {
    label: 'tickets / open',
    columns: [
      { key: 'name', label: 'Ticket' },
      { key: 'priority', label: 'Priority' },
      { key: 'status', label: 'Status' },
      { key: 'owner', label: 'Owner' },
      { key: 'updated', label: 'Updated' },
    ],
    rows: [
      {
        id: '1',
        avatar: { initials: 'AR', tone: 'rose' },
        cells: {
          name: 'API rate limit issue',
          priority: { kind: 'pill', label: 'High', tone: 'rose' },
          status: { kind: 'pill', label: 'Open', tone: 'sky' },
          owner: { kind: 'muted', value: 'Sarah C.' },
          updated: { kind: 'muted', value: '2h ago' },
        },
      },
      {
        id: '2',
        avatar: { initials: 'LF', tone: 'amber' },
        cells: {
          name: 'Login flow broken on Safari',
          priority: { kind: 'pill', label: 'Critical', tone: 'rose' },
          status: { kind: 'pill', label: 'In Progress', tone: 'amber' },
          owner: { kind: 'muted', value: 'Daniel P.' },
          updated: { kind: 'muted', value: '30m ago' },
        },
      },
      {
        id: '3',
        avatar: { initials: 'CE', tone: 'sky' },
        cells: {
          name: 'Add CSV export',
          priority: { kind: 'pill', label: 'Low', tone: 'slate' },
          status: { kind: 'pill', label: 'Backlog', tone: 'slate' },
          owner: { kind: 'muted', value: 'Unassigned' },
          updated: { kind: 'muted', value: '3d ago' },
        },
      },
      {
        id: '4',
        avatar: { initials: 'BD', tone: 'mint' },
        cells: {
          name: 'Billing discrepancy report',
          priority: { kind: 'pill', label: 'Medium', tone: 'amber' },
          status: { kind: 'pill', label: 'In Review', tone: 'lilac' },
          owner: { kind: 'muted', value: 'Liam O.' },
          updated: { kind: 'muted', value: 'Yesterday' },
        },
      },
      {
        id: '5',
        avatar: { initials: 'BR', tone: 'lilac' },
        cells: {
          name: 'Feature: bulk reassign',
          priority: { kind: 'pill', label: 'Low', tone: 'slate' },
          status: { kind: 'pill', label: 'Open', tone: 'sky' },
          owner: { kind: 'muted', value: 'Priya S.' },
          updated: { kind: 'muted', value: '5d ago' },
        },
      },
    ],
  },
}

const collections: ModuleLandingConfig = {
  slug: 'collections',
  title: 'Collections',
  subtitle:
    'Custom lists, libraries, and registries — built without a developer and shaped exactly the way your team thinks.',
  icon: StackIcon,
  accent: 'amber',
  features: [
    {
      icon: GearSixIcon,
      title: 'Custom fields',
      body: 'Text, numbers, selects, relations, formulas. Add fields without migrations or downtime.',
    },
    {
      icon: ListChecksIcon,
      title: 'Views per role',
      body: 'Save and share filtered views. Let sales see one thing, ops see another, and accounting see a third.',
    },
    {
      icon: LightningIcon,
      title: 'Automations',
      body: 'Trigger workflows on row changes — assign, notify, sync — without writing a Zapier zap.',
    },
    {
      icon: CheckSquareIcon,
      title: 'Permissions you trust',
      body: 'Field-level + row-level access. Sensitive data stays sensitive, even on shared collections.',
    },
  ],
  preview: {
    label: 'collections / shared',
    columns: [
      { key: 'name', label: 'Collection' },
      { key: 'kind', label: 'Type' },
      { key: 'updated', label: 'Updated' },
      { key: 'viewers', label: 'Viewers', align: 'right' },
    ],
    rows: [
      {
        id: '1',
        avatar: { initials: 'MA', tone: 'amber' },
        cells: {
          name: 'Marketing Assets',
          kind: { kind: 'muted', value: 'Collection · 247 items' },
          updated: { kind: 'muted', value: '2h ago' },
          viewers: { kind: 'money', value: '4' },
        },
      },
      {
        id: '2',
        avatar: { initials: 'BG', tone: 'sky' },
        cells: {
          name: 'Brand Guidelines',
          kind: { kind: 'muted', value: 'Doc · 12 entries' },
          updated: { kind: 'muted', value: 'Yesterday' },
          viewers: { kind: 'money', value: '12' },
        },
      },
      {
        id: '3',
        avatar: { initials: 'RL', tone: 'rose' },
        cells: {
          name: 'Recipe Library',
          kind: { kind: 'muted', value: 'Custom · 89 items' },
          updated: { kind: 'muted', value: '3d ago' },
          viewers: { kind: 'money', value: '8' },
        },
      },
      {
        id: '4',
        avatar: { initials: 'VD', tone: 'mint' },
        cells: {
          name: 'Vendor Database',
          kind: { kind: 'muted', value: 'Custom · 156 items' },
          updated: { kind: 'muted', value: '1w ago' },
          viewers: { kind: 'money', value: '6' },
        },
      },
      {
        id: '5',
        avatar: { initials: 'OK', tone: 'lilac' },
        cells: {
          name: 'Q1 OKRs',
          kind: { kind: 'muted', value: 'Doc · 34 entries' },
          updated: { kind: 'muted', value: '5h ago' },
          viewers: { kind: 'money', value: '18' },
        },
      },
    ],
  },
}

/**
 * Default catch-all — no preview table, generic copy.
 * Used when SubscriptionFeatureGuard receives a feature key we don't have
 * a tailored landing for.
 */
export const DEFAULT_LANDING_CONFIG: ModuleLandingConfig = {
  slug: 'default',
  eyebrow: 'Pro feature',
  title: 'This feature is part of Pro',
  subtitle:
    'You’ve found a corner of Scrambl that’s reserved for Pro plans. Upgrade once, unlock all of it — including the modules in your sidebar marked Pro.',
  icon: SparkleIcon,
  accent: 'amber',
  features: [
    {
      icon: LightningIcon,
      title: 'Every module unlocked',
      body: 'Forms, Contacts, Deals, Team, Suppliers, Contracts, Events, and more — all included.',
    },
    {
      icon: UsersThreeIcon,
      title: 'Bigger teams, real workflows',
      body: 'Up to 50 seats, unlimited projects, custom fields, and templates. No surprise gates.',
    },
    {
      icon: ShieldCheckIcon,
      title: 'Pro-grade controls',
      body: 'Workflows, integrations, data export, watermark removal, and form CAPTCHA.',
    },
    {
      icon: SparkleIcon,
      title: 'More AI credits',
      body: '4× the monthly Benny credits, plus priority access to new AI capabilities.',
    },
  ],
}

export const MODULE_LANDING_CONFIGS: Record<string, ModuleLandingConfig> = {
  forms,
  contacts,
  deals,
  team,
  suppliers,
  contracts,
  events,
  tickets,
  collections,
}

export function getModuleLandingConfig(
  featureKey: string,
  options: ModuleLandingConfigOptions = {},
): ModuleLandingConfig {
  if (featureKey === 'suppliers') {
    return buildSuppliersLandingConfig(options.supplierTerminology)
  }

  return MODULE_LANDING_CONFIGS[featureKey] ?? DEFAULT_LANDING_CONFIG
}
